/**
 * Composants tilt 3D & motion premium :
 * - TiltCard3D : la card s'incline réellement vers le curseur (perspective + rotateX/Y)
 *                avec spring physics via framer-motion
 * - PageTransition : enveloppe une route pour l'animer en entrée/sortie
 * - MagneticButton : le bouton suit légèrement le curseur (effet aimanté)
 */
import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";

const springConfig = { stiffness: 300, damping: 30, mass: 0.6 };

/**
 * Card qui s'incline en 3D selon la position de la souris.
 * - Rotation max ±10° (intense mais pas malade)
 * - Spring physics pour le retour fluide
 * - Glow radial qui suit aussi le curseur
 */
export function TiltCard3D({ children, className = "", intensity = 10, glow = true, ...rest }) {
  const ref = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [intensity, -intensity]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-intensity, intensity]), springConfig);

  // Glow position
  const glowX = useTransform(mouseX, [-0.5, 0.5], ["0%", "100%"]);
  const glowY = useTransform(mouseY, [-0.5, 0.5], ["0%", "100%"]);

  const onMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };
  const onMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{
        rotateX, rotateY,
        transformStyle: "preserve-3d",
        transformPerspective: 1000,
      }}
      whileTap={{ scale: 0.98 }}
      className={`relative overflow-hidden ${className}`}
      {...rest}
    >
      {/* Glow radial qui suit la souris */}
      {glow && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100"
          style={{
            background: `radial-gradient(circle at ${glowX} ${glowY}, rgba(90,145,122,0.22), transparent 50%)`,
            transition: "opacity 300ms",
          }}
        />
      )}
      <div style={{ transform: "translateZ(20px)" }}>{children}</div>
    </motion.div>
  );
}

/**
 * Bouton magnétique : suit légèrement le curseur quand on s'en approche.
 */
export function MagneticButton({ children, className = "", as: Tag = "button", ...rest }) {
  const ref = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const x = useSpring(mouseX, { stiffness: 250, damping: 18 });
  const y = useSpring(mouseY, { stiffness: 250, damping: 18 });
  const MotionTag = motion[Tag] || motion.button;

  const onMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    mouseX.set((e.clientX - cx) * 0.25);
    mouseY.set((e.clientY - cy) * 0.25);
  };
  const onMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <MotionTag
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{ x, y }}
      whileTap={{ scale: 0.94 }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * Transition de page : fade + slide léger.
 */
export function PageTransition({ children, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Reveal staggered avec spring fluide.
 */
export function StaggerReveal({ children, delay = 0.05, className = "" }) {
  const items = React.Children.toArray(children);
  return (
    <div className={className}>
      {items.map((child, i) => (
        <motion.div
          key={child.key ?? i}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {child}
        </motion.div>
      ))}
    </div>
  );
}

export { AnimatePresence };
